import { useQuery } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { Card, ErrorNote, PageHeader, Segmented, SkeletonCard, Stat, TD, TH, cx } from '../components/ui';
import { api } from '../lib/api';
import { CHANNEL_LABEL, type AnyChannel, type DashboardAnalytics } from '../lib/dashboard';
import { formatDuration } from '../lib/format';

type Window = '7' | '30' | '90';

export function AnalyticsPage() {
  const [win, setWin] = useState<Window>('90');
  const q = useQuery({ queryKey: ['dashboard', 'analytics', win], queryFn: () => api.get<DashboardAnalytics>(`/api/dashboard/analytics?days=${win}`) });
  const a = q.data;
  return (
    <>
      <PageHeader
        eyebrow="Analytics"
        title="How the pipeline performs"
        subtitle="Sources, scores, verdicts and knockouts across every job."
        actions={
          <Segmented<Window>
            label="Time window"
            value={win}
            onChange={setWin}
            options={[
              { value: '7', label: '7 days' },
              { value: '30', label: '30 days' },
              { value: '90', label: 'Quarter' },
            ]}
          />
        }
      />
      <ErrorNote error={q.error} />
      {q.isLoading && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <SkeletonCard key={i} lines={2} />
          ))}
        </div>
      )}
      {a && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
            <Stat label="Scored interviews" value={a.scored} icon="headphones" accent />
            <Stat label="Average interview" value={a.avgInterviewSeconds === null ? '—' : formatDuration(a.avgInterviewSeconds * 1000)} icon="clock" note={durationNote(a.durationByChannel)} />
            <Stat label="Lead sources" value={a.conversionBySource.length} icon="layers" />
          </div>

          <Card title="Interviews per day" description="Started against completed, every day in the window." actions={<Legend />}>
            <PerDayChart rows={a.interviewsPerDay} />
          </Card>

          <div className="grid gap-4 xl:grid-cols-2">
            <Card title="Score distribution" description="Scored interviews in ten-point buckets.">
              <Histogram rows={a.scoreDistribution} />
            </Card>
            <Card title="Conversion by source" description="Which lead sources turn into qualified candidates.">
              <SourceTable rows={a.conversionBySource} />
            </Card>
            <Card title="Verdicts by job">
              <VerdictBars rows={a.verdictByJob} />
            </Card>
            <Card title="Knockouts failed" description="The rules that take candidates out most often.">
              <HBars rows={a.knockoutFrequencies.map((k) => ({ label: k.message, value: k.count }))} empty="No knockout rule has failed in this window." />
            </Card>
          </div>
        </div>
      )}
    </>
  );
}

function durationNote(d: DashboardAnalytics['durationByChannel']): string | undefined {
  const parts = (Object.entries(d) as Array<[AnyChannel, number]>).map(([ch, s]) => `${CHANNEL_LABEL[ch]} ${formatDuration(s * 1000)}`);
  return parts.length ? parts.join(' · ') : undefined;
}

function Legend() {
  return (
    <span className="flex items-center gap-3 text-xs text-muted-fg">
      <span className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-[3px] bg-accent/30" /> Started
      </span>
      <span className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-[3px] bg-brand-gradient" /> Completed
      </span>
    </span>
  );
}

function BrandGradient({ id }: { id: string }) {
  return (
    <defs>
      <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#d6217f" />
        <stop offset="1" stopColor="#7e2ea6" />
      </linearGradient>
    </defs>
  );
}

/** Grouped bars per day, drawn in SVG. Each bar carries a <title> for hover and screen readers. */
function PerDayChart({ rows }: { rows: DashboardAnalytics['interviewsPerDay'] }) {
  const grad = useId();
  const W = 900;
  const H = 200;
  const pad = { l: 28, r: 8, t: 8, b: 22 };
  const max = Math.max(1, ...rows.map((r) => Math.max(r.started, r.completed)));
  const step = (W - pad.l - pad.r) / Math.max(1, rows.length);
  const bw = Math.max(2, step * 0.36);
  const y = (v: number) => pad.t + (H - pad.t - pad.b) * (1 - v / max);
  const total = rows.reduce((n, r) => n + r.completed, 0);
  const ticks = [0, Math.round(max / 2), max].filter((v, i, arr) => arr.indexOf(v) === i);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="h-52 w-full" role="img" aria-label={`Interviews per day over the last ${rows.length} days; ${total} completed in total.`}>
      <BrandGradient id={grad} />
      {ticks.map((t) => (
        <g key={t}>
          <line x1={pad.l} x2={W - pad.r} y1={y(t)} y2={y(t)} className="stroke-border" strokeWidth={1} strokeDasharray="2 4" />
          <text x={pad.l - 6} y={y(t) + 4} textAnchor="end" className="fill-muted-fg font-mono text-[10px]">
            {t}
          </text>
        </g>
      ))}
      {rows.map((r, i) => {
        const x = pad.l + i * step + (step - bw * 2) / 2;
        return (
          <g key={r.date}>
            <rect x={x} y={y(r.started)} width={bw} height={y(0) - y(r.started)} rx={2} className="fill-accent/30">
              <title>{`${r.date}: ${r.started} started`}</title>
            </rect>
            <rect x={x + bw} y={y(r.completed)} width={bw} height={y(0) - y(r.completed)} rx={2} fill={`url(#${grad})`}>
              <title>{`${r.date}: ${r.completed} completed`}</title>
            </rect>
            {(i % 5 === 0 || i === rows.length - 1) && (
              <text x={x + bw} y={H - 6} textAnchor="middle" className="fill-muted-fg font-mono text-[10px]">
                {r.date.slice(5)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function Histogram({ rows }: { rows: DashboardAnalytics['scoreDistribution'] }) {
  const W = 420;
  const H = 180;
  const pad = { l: 8, r: 8, t: 16, b: 22 };
  const max = Math.max(1, ...rows.map((r) => r.count));
  const step = (W - pad.l - pad.r) / rows.length;
  const total = rows.reduce((n, r) => n + r.count, 0);
  if (total === 0) return <p className="text-sm text-muted-fg">No scored interviews in this window.</p>;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="h-48 w-full" role="img" aria-label={`Score distribution of ${total} scored interviews in ten-point buckets.`}>
      {rows.map((r, i) => {
        const h = ((H - pad.t - pad.b) * r.count) / max;
        const x = pad.l + i * step + 3;
        // Ten-point buckets, coloured like the score bars: 70+ strong, 50-69 borderline, below 50 weak.
        const fill = r.from >= 70 ? 'fill-success' : r.from >= 50 ? 'fill-warning' : 'fill-destructive';
        return (
          <g key={r.from}>
            <rect x={x} y={H - pad.b - h} width={step - 6} height={h} rx={4} className={`${fill} opacity-80`}>
              <title>{`${r.from}–${r.to}: ${r.count}`}</title>
            </rect>
            {r.count > 0 && (
              <text x={x + (step - 6) / 2} y={H - pad.b - h - 4} textAnchor="middle" className="fill-fg font-mono text-[10px]">
                {r.count}
              </text>
            )}
            <text x={x + (step - 6) / 2} y={H - 6} textAnchor="middle" className="fill-muted-fg font-mono text-[10px]">
              {r.from}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function SourceTable({ rows }: { rows: DashboardAnalytics['conversionBySource'] }) {
  if (rows.length === 0) return <p className="text-sm text-muted-fg">No leads in this window.</p>;
  return (
    <table className="-mx-2 w-[calc(100%+1rem)] text-sm">
      <thead>
        <tr>
          <th className={cx(TH, 'px-2')}>Source</th>
          <th className={cx(TH, 'px-2 text-right')}>Leads</th>
          <th className={cx(TH, 'px-2 text-right')}>Interviewed</th>
          <th className={cx(TH, 'px-2 text-right')}>Qualified</th>
          <th className={cx(TH, 'w-1/3 px-2')}>Rate</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-border">
        {rows.map((r) => (
          <tr key={r.source}>
            <td className={cx(TD, 'px-2 font-medium text-fg')}>{r.source}</td>
            <td className={cx(TD, 'px-2 text-right font-mono tabular')}>{r.leads}</td>
            <td className={cx(TD, 'px-2 text-right font-mono tabular')}>{r.interviewed}</td>
            <td className={cx(TD, 'px-2 text-right font-mono tabular')}>{r.qualified}</td>
            <td className={cx(TD, 'px-2')}>
              <div className="flex items-center gap-2">
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full bg-brand-gradient" style={{ width: `${Math.min(100, r.rate)}%` }} />
                </div>
                <span className="w-12 text-right font-mono text-xs tabular">{r.rate}%</span>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function VerdictBars({ rows }: { rows: DashboardAnalytics['verdictByJob'] }) {
  if (rows.length === 0) return <p className="text-sm text-muted-fg">No verdicts in this window.</p>;
  return (
    <div className="space-y-4">
      <div className="flex gap-4 text-xs text-muted-fg">
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-[3px] bg-success" /> Qualified
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-[3px] bg-warning" /> Needs review
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-[3px] bg-destructive" /> Not qualified
        </span>
      </div>
      {rows.map((r) => {
        const total = r.qualified + r.needsReview + r.notQualified;
        const seg = (n: number) => `${(n / total) * 100}%`;
        return (
          <div key={r.jobId}>
            <div className="flex justify-between text-sm">
              <span className="truncate font-medium text-fg">{r.title}</span>
              <span className="font-mono text-xs text-muted-fg">
                {r.qualified} / {r.needsReview} / {r.notQualified}
              </span>
            </div>
            <div className="mt-1.5 flex h-3 gap-0.5 overflow-hidden rounded-full" role="img" aria-label={`${r.title}: ${r.qualified} qualified, ${r.needsReview} needs review, ${r.notQualified} not qualified`}>
              {r.qualified > 0 && <div className="bg-success" style={{ width: seg(r.qualified) }} />}
              {r.needsReview > 0 && <div className="bg-warning" style={{ width: seg(r.needsReview) }} />}
              {r.notQualified > 0 && <div className="bg-destructive" style={{ width: seg(r.notQualified) }} />}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function HBars({ rows, empty }: { rows: Array<{ label: string; value: number }>; empty: string }) {
  if (rows.length === 0) return <p className="text-sm text-muted-fg">{empty}</p>;
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <ul className="space-y-3">
      {rows.slice(0, 8).map((r) => (
        <li key={r.label} className="text-sm">
          <div className="flex justify-between gap-3">
            <span className="truncate text-fg">{r.label}</span>
            <span className="font-mono text-xs tabular text-muted-fg">{r.value}</span>
          </div>
          <div className="mt-1 h-2 overflow-hidden rounded-full bg-muted" aria-hidden>
            <div className="h-full rounded-full bg-brand-gradient" style={{ width: `${(r.value / max) * 100}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}
